import { EASY_MODE_INITIAL_BOLTS, RATING_BONUS } from '../gameConstants';
import { calculateXP, HASTE_THRESHOLD_SECONDS, type AttemptNumber } from '@/utils/xp';
import type {
  AutoRevealReason,
  GameRunMechanicsCommand,
  GameRunMechanicsCreateInput,
  GameRunMechanicsState,
  GameRunMechanicsView,
  MechanicsTransitionResult,
  PersistedQuestionXPFact,
  RunQuestionHistoryEntry,
  SessionXPBreakdown,
  XPBreakdown,
} from './types';

export const POLL_PARTICIPATION_XP = 10;

const createEmptyQuestionBreakdown = (): XPBreakdown => ({
  correctXP: 0,
  speedBonus: 0,
  streakBonus: 0,
  hastePenalty: 0,
  hastePenalties: [],
  ratingBonus: 0,
});

const createEmptySessionBreakdown = (): SessionXPBreakdown => ({
  correctXP: 0,
  speedBonus: 0,
  streakBonus: 0,
  hastePenalty: 0,
  ratingBonus: 0,
});

const cloneQuestionBreakdown = (breakdown: XPBreakdown): XPBreakdown => ({
  ...breakdown,
  hastePenalties: [...breakdown.hastePenalties],
});

const cloneSessionBreakdown = (breakdown: SessionXPBreakdown): SessionXPBreakdown => ({
  ...breakdown,
});

const hasScoredXPComponents = (breakdown: SessionXPBreakdown): boolean =>
  breakdown.correctXP !== 0 ||
  breakdown.speedBonus !== 0 ||
  breakdown.streakBonus !== 0 ||
  breakdown.hastePenalty !== 0 ||
  (breakdown.ratingBonus ?? 0) !== 0;

export function createGameRunMechanics(input: GameRunMechanicsCreateInput): GameRunMechanicsState {
  return {
    mode: input.mode,
    difficulty: input.difficulty,
    guessSequence: [],
    incorrectAttempts: [],
    currentStreak: 0,
    longestStreak: 0,
    strikes: 0,
    strikesForCurrentQuestion: false,
    bolts: input.difficulty === 'easy' ? EASY_MODE_INITIAL_BOLTS : 0,
    eliminatedDistractors: [],
    boltUsedThisQuestion: false,
    boltUsageCount: 0,
    questionsAnswered: 0,
    correctAnswers: 0,
    attemptCounts: { firstTry: 0, secondTry: 0, thirdTry: 0, fourthTry: 0 },
    questionStartedAt: input.questionStartedAt,
    correctAnswerElapsedSeconds: undefined,
    xpEventId: 0,
    currentQuestionXPBreakdown: undefined,
    currentQuestionHastePenalties: [],
    sessionTotalXP: 0,
    sessionXPBreakdown: createEmptySessionBreakdown(),
    sessionXPBreakdownAvailable: true,
    currentQuestionNetXP: 0,
    lastAnswerWasHasty: false,
    lastXPEvent: undefined,
    runQuestionHistory: [],
    currentPollDistribution: undefined,
    currentPollSelectedOptionId: undefined,
    ratingXPAwarded: false,
    focusLostDuringQuestion: false,
    wasAutoRevealed: false,
    autoRevealReason: undefined,
    lastRewardStreak: 0,
    hasReceivedInitialBoltBonus: false,
    currentQuestionStatus: 'active',
    hasStartedInitialQuestionClock: false,
  };
}

function getElapsedSeconds(mechanics: GameRunMechanicsState, answeredAt: number): number {
  return Math.max(0, answeredAt - mechanics.questionStartedAt) / 1_000;
}

function getLastEventBreakdown(breakdown: XPBreakdown): XPBreakdown {
  return {
    correctXP: breakdown.correctXP,
    speedBonus: breakdown.speedBonus,
    streakBonus: breakdown.streakBonus,
    hastePenalty: breakdown.hastePenalty,
    hastePenalties: [...breakdown.hastePenalties],
  };
}

function answerSelected(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'ANSWER_SELECTED' }>
): MechanicsTransitionResult {
  if (
    mechanics.currentQuestionStatus === 'resolved' ||
    mechanics.currentQuestionStatus === 'finalized'
  ) {
    return { mechanics, outcome: { type: 'no-op', reason: 'question-not-active' } };
  }

  const isSkip = !!command.isSkip;
  const isValidAnswerIndex =
    Number.isInteger(command.answerIndex) && command.answerIndex >= 0 && command.answerIndex <= 3;
  if ((isSkip && command.answerIndex !== -1) || (!isSkip && !isValidAnswerIndex)) {
    return { mechanics, outcome: { type: 'no-op', reason: 'invalid-answer-index' } };
  }

  const correctAnswerIndex = command.question.correctAnswerIndex ?? 0;
  const isCorrect = command.answerIndex === correctAnswerIndex;
  if (!isSkip && !isCorrect && mechanics.guessSequence.includes(command.answerIndex)) {
    return { mechanics, outcome: { type: 'no-op', reason: 'answer-already-selected' } };
  }

  const isFromEmail = !!command.isFromEmail;
  const isEndless = mechanics.mode === 'endless';
  const elapsedSeconds = getElapsedSeconds(mechanics, command.answeredAt);
  const guessSequence =
    isSkip || (isCorrect && mechanics.guessSequence.includes(correctAnswerIndex))
      ? [...mechanics.guessSequence]
      : [...mechanics.guessSequence, command.answerIndex];
  const incorrectAttempts = isCorrect
    ? [...mechanics.incorrectAttempts]
    : [...mechanics.incorrectAttempts, command.selectedAnswerText];

  let currentStreak = mechanics.currentStreak;
  let longestStreak = mechanics.longestStreak;
  let strikes = mechanics.strikes;
  let strikesForCurrentQuestion = mechanics.strikesForCurrentQuestion;
  let bolts = mechanics.bolts;
  let lastRewardStreak = mechanics.lastRewardStreak;
  let hasReceivedInitialBoltBonus = mechanics.hasReceivedInitialBoltBonus;

  if (isEndless) {
    if (isCorrect && !isSkip) {
      // A restored active sequence can already include the correct index even
      // though the XP and streak result still need to be applied.
      const selectedAnswerAlreadyRecorded = mechanics.guessSequence.includes(command.answerIndex);
      const effectiveAttempts =
        mechanics.guessSequence.length +
        (selectedAnswerAlreadyRecorded ? 0 : 1) +
        mechanics.boltUsageCount;
      const isFirstAttemptNoHelp = effectiveAttempts === 1 && !mechanics.boltUsedThisQuestion;

      if (isFirstAttemptNoHelp) {
        currentStreak += 1;
        longestStreak = Math.max(longestStreak, currentStreak);
        strikes = Math.max(0, strikes - 1);

        if (currentStreak === 2 && !hasReceivedInitialBoltBonus) {
          bolts += 2;
          hasReceivedInitialBoltBonus = true;
          lastRewardStreak = 2;
        } else if (currentStreak === lastRewardStreak + 5) {
          bolts += 1;
          lastRewardStreak = currentStreak;
        }
      }
    } else if (!isSkip) {
      currentStreak = 0;
      lastRewardStreak = 0;

      if (mechanics.difficulty === 'easy') {
        if (!strikesForCurrentQuestion) {
          strikes = Math.max(0, strikes + 1);
          strikesForCurrentQuestion = true;
        }
      } else {
        strikes = Math.max(0, strikes + 1);
      }
    }
  }

  const difficultyTheta = command.question.difficultyTheta ?? 0;
  let next: GameRunMechanicsState = {
    ...mechanics,
    guessSequence,
    incorrectAttempts,
    currentStreak,
    longestStreak,
    strikes,
    strikesForCurrentQuestion,
    bolts,
    lastRewardStreak,
    hasReceivedInitialBoltBonus,
    currentQuestionXPBreakdown: mechanics.currentQuestionXPBreakdown
      ? cloneQuestionBreakdown(mechanics.currentQuestionXPBreakdown)
      : createEmptyQuestionBreakdown(),
  };

  if (isCorrect && !isSkip) {
    const attemptCount = Math.min(guessSequence.length, 4) as AttemptNumber;
    const streakForXP = mechanics.mode === 'daily_challenge' ? 0 : mechanics.currentStreak;
    const calculated = calculateXP(
      difficultyTheta,
      elapsedSeconds,
      streakForXP,
      attemptCount,
      false,
      mechanics.focusLostDuringQuestion,
      mechanics.lastAnswerWasHasty
    );
    const speedBonus = isFromEmail ? 0 : calculated.speedBonus;
    const awardedXP = calculated.correctXP + speedBonus + calculated.streakBonus;
    const baseBreakdown = next.currentQuestionXPBreakdown ?? createEmptyQuestionBreakdown();
    const xpEventId = mechanics.xpEventId + 1;
    const eventBreakdown: XPBreakdown = {
      correctXP: calculated.correctXP,
      speedBonus,
      streakBonus: calculated.streakBonus,
      hastePenalty: 0,
      hastePenalties: [],
    };

    next = {
      ...next,
      correctAnswerElapsedSeconds: elapsedSeconds,
      lastAnswerWasHasty: isFromEmail ? false : elapsedSeconds < HASTE_THRESHOLD_SECONDS,
      xpEventId,
      lastXPEvent: { id: xpEventId, amount: awardedXP, breakdown: eventBreakdown },
      currentQuestionXPBreakdown: {
        ...baseBreakdown,
        correctXP: baseBreakdown.correctXP + calculated.correctXP,
        speedBonus: baseBreakdown.speedBonus + speedBonus,
        streakBonus: baseBreakdown.streakBonus + calculated.streakBonus,
      },
      currentQuestionHastePenalties: [],
      sessionTotalXP: mechanics.sessionTotalXP + awardedXP,
      currentQuestionNetXP: mechanics.currentQuestionNetXP + awardedXP,
      sessionXPBreakdown: {
        ...mechanics.sessionXPBreakdown,
        correctXP: mechanics.sessionXPBreakdown.correctXP + calculated.correctXP,
        speedBonus: mechanics.sessionXPBreakdown.speedBonus + speedBonus,
        streakBonus: mechanics.sessionXPBreakdown.streakBonus + calculated.streakBonus,
      },
      currentQuestionStatus: 'resolved',
    };
  } else if (!isSkip && !isFromEmail) {
    const calculated = calculateXP(
      difficultyTheta,
      elapsedSeconds,
      0,
      1,
      true,
      false,
      mechanics.lastAnswerWasHasty
    );

    if (calculated.hastePenalty > 0) {
      const baseBreakdown = next.currentQuestionXPBreakdown ?? createEmptyQuestionBreakdown();
      const hastePenalties = [...mechanics.currentQuestionHastePenalties, calculated.hastePenalty];
      const xpEventId = mechanics.xpEventId + 1;
      const eventBreakdown: XPBreakdown = {
        correctXP: 0,
        speedBonus: 0,
        streakBonus: 0,
        hastePenalty: calculated.hastePenalty,
        hastePenalties: [calculated.hastePenalty],
      };

      next = {
        ...next,
        xpEventId,
        lastXPEvent: {
          id: xpEventId,
          amount: -calculated.hastePenalty,
          breakdown: eventBreakdown,
        },
        currentQuestionXPBreakdown: {
          ...baseBreakdown,
          hastePenalty: baseBreakdown.hastePenalty + calculated.hastePenalty,
          hastePenalties: [...baseBreakdown.hastePenalties, calculated.hastePenalty],
        },
        currentQuestionHastePenalties: hastePenalties,
        sessionTotalXP: mechanics.sessionTotalXP - calculated.hastePenalty,
        currentQuestionNetXP: mechanics.currentQuestionNetXP - calculated.hastePenalty,
        sessionXPBreakdown: {
          ...mechanics.sessionXPBreakdown,
          hastePenalty: mechanics.sessionXPBreakdown.hastePenalty + calculated.hastePenalty,
        },
      };
    }
  }

  let autoRevealReason: AutoRevealReason | undefined;
  if (!isCorrect && !isSkip && isEndless && next.strikes >= 3) {
    autoRevealReason = 'final_strike';
  } else if (
    !isCorrect &&
    !isSkip &&
    isEndless &&
    mechanics.difficulty === 'easy' &&
    next.incorrectAttempts.length > 0
  ) {
    autoRevealReason = 'easy_mode';
  }

  if (autoRevealReason) {
    next = {
      ...next,
      autoRevealReason,
      currentQuestionStatus: 'auto_reveal_pending',
    };
  } else if (isCorrect) {
    next = {
      ...next,
      wasAutoRevealed: false,
      autoRevealReason: undefined,
      currentQuestionStatus: 'resolved',
    };
  }

  return {
    mechanics: next,
    outcome: {
      type: 'answer-selected',
      answer: isSkip ? 'skipped' : isCorrect ? 'correct' : 'incorrect',
      feedbackReady: isCorrect,
      autoRevealReason,
    },
  };
}

function pollAnswered(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'POLL_ANSWERED' }>
): MechanicsTransitionResult {
  const alreadyAnswered = command.alreadyAnswered || !!mechanics.currentPollSelectedOptionId;
  const wasAlreadyRecorded = !!mechanics.currentPollSelectedOptionId;
  const awardedXP = alreadyAnswered ? 0 : command.xpAwarded;
  const xpEventId = awardedXP > 0 ? mechanics.xpEventId + 1 : mechanics.xpEventId;

  return {
    mechanics: {
      ...mechanics,
      currentPollDistribution: command.distribution.map((entry) => ({ ...entry })),
      currentPollSelectedOptionId: command.optionId,
      currentQuestionNetXP: wasAlreadyRecorded ? mechanics.currentQuestionNetXP : awardedXP,
      currentQuestionXPBreakdown: createEmptyQuestionBreakdown(),
      sessionTotalXP: mechanics.sessionTotalXP + awardedXP,
      sessionXPBreakdownAvailable: false,
      xpEventId,
      lastXPEvent: awardedXP > 0 ? { id: xpEventId, amount: awardedXP } : mechanics.lastXPEvent,
      currentQuestionStatus: 'resolved',
    },
    outcome: { type: 'poll-answered', awardedXP, alreadyAnswered },
  };
}

function submitRating(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'SUBMIT_RATING' }>
): MechanicsTransitionResult {
  if (mechanics.ratingXPAwarded || command.rating === 0) {
    return {
      mechanics,
      outcome: { type: 'rating-submitted', awardedXP: 0 },
    };
  }

  const baseBreakdown = mechanics.currentQuestionXPBreakdown
    ? cloneQuestionBreakdown(mechanics.currentQuestionXPBreakdown)
    : createEmptyQuestionBreakdown();
  const xpEventId = mechanics.xpEventId + 1;

  return {
    mechanics: {
      ...mechanics,
      ratingXPAwarded: true,
      sessionTotalXP: mechanics.sessionTotalXP + RATING_BONUS,
      currentQuestionNetXP: mechanics.currentQuestionNetXP + RATING_BONUS,
      xpEventId,
      currentQuestionXPBreakdown: {
        ...baseBreakdown,
        ratingBonus: (baseBreakdown.ratingBonus ?? 0) + RATING_BONUS,
      },
      sessionXPBreakdown: {
        ...mechanics.sessionXPBreakdown,
        ratingBonus: (mechanics.sessionXPBreakdown.ratingBonus ?? 0) + RATING_BONUS,
      },
      lastXPEvent: {
        id: xpEventId,
        amount: RATING_BONUS,
        breakdown: getLastEventBreakdown(baseBreakdown),
      },
    },
    outcome: { type: 'rating-submitted', awardedXP: RATING_BONUS },
  };
}

export function canUseBolt(mechanics: GameRunMechanicsState): boolean {
  return (
    mechanics.mode === 'endless' &&
    (mechanics.currentQuestionStatus === 'active' ||
      mechanics.currentQuestionStatus === 'auto_reveal_pending') &&
    mechanics.bolts > 0 &&
    mechanics.eliminatedDistractors.length < 3 &&
    mechanics.strikes < 3
  );
}

function applyBolt(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'USE_BOLT' }>
): MechanicsTransitionResult {
  if (!canUseBolt(mechanics)) {
    return { mechanics, outcome: { type: 'no-op', reason: 'bolt-unavailable' } };
  }

  const available = [1, 2, 3].filter(
    (answerIndex) =>
      !mechanics.eliminatedDistractors.includes(answerIndex) &&
      !mechanics.guessSequence.includes(answerIndex)
  );
  if (available.length === 0) {
    return { mechanics, outcome: { type: 'no-op', reason: 'no-bolt-target' } };
  }

  const random = Number.isFinite(command.random)
    ? Math.min(Math.max(command.random, 0), 1 - Number.EPSILON)
    : 0;
  const eliminatedDistractor = available[Math.floor(random * available.length)];
  const eliminatedDistractors = [...mechanics.eliminatedDistractors, eliminatedDistractor];
  const autoRevealed = eliminatedDistractors.length >= 3;

  return {
    mechanics: {
      ...mechanics,
      bolts: mechanics.bolts - 1,
      eliminatedDistractors,
      boltUsedThisQuestion: true,
      boltUsageCount: mechanics.boltUsageCount + 1,
      ...(autoRevealed
        ? {
            guessSequence: mechanics.guessSequence.includes(0)
              ? [...mechanics.guessSequence]
              : [...mechanics.guessSequence, 0],
            wasAutoRevealed: true,
            autoRevealReason: 'all_distractors_eliminated' as const,
            currentQuestionStatus: 'resolved' as const,
          }
        : {}),
    },
    outcome: { type: 'bolt-used', eliminatedDistractor, autoRevealed },
  };
}

function setDifficulty(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'SET_DIFFICULTY' }>
): MechanicsTransitionResult {
  if (mechanics.difficulty === command.difficulty) {
    return { mechanics, outcome: { type: 'no-op', reason: 'difficulty-unchanged' } };
  }

  return {
    mechanics: {
      ...mechanics,
      difficulty: command.difficulty,
      strikesForCurrentQuestion:
        command.difficulty === 'standard' ? false : mechanics.strikesForCurrentQuestion,
      bolts:
        command.difficulty === 'easy'
          ? Math.max(mechanics.bolts, EASY_MODE_INITIAL_BOLTS)
          : mechanics.bolts,
    },
    outcome: { type: 'difficulty-changed', difficulty: command.difficulty },
  };
}

function completeAutoReveal(mechanics: GameRunMechanicsState): MechanicsTransitionResult {
  if (mechanics.currentQuestionStatus !== 'auto_reveal_pending' || !mechanics.autoRevealReason) {
    return { mechanics, outcome: { type: 'no-op', reason: 'auto-reveal-not-pending' } };
  }

  return {
    mechanics: {
      ...mechanics,
      guessSequence: mechanics.guessSequence.includes(0)
        ? [...mechanics.guessSequence]
        : [...mechanics.guessSequence, 0],
      wasAutoRevealed: true,
      currentQuestionStatus: 'resolved',
    },
    outcome: { type: 'auto-reveal-completed', reason: mechanics.autoRevealReason },
  };
}

function completeCurrentQuestion(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'COMPLETE_CURRENT_QUESTION' }>
): MechanicsTransitionResult {
  if (mechanics.currentQuestionStatus === 'finalized') {
    return { mechanics, outcome: { type: 'no-op', reason: 'question-already-finalized' } };
  }

  const historyEntry: RunQuestionHistoryEntry = {
    questionId: command.questionId,
    questionIndex: command.questionIndex,
    netXP: mechanics.currentQuestionNetXP,
    breakdown: mechanics.currentQuestionXPBreakdown
      ? cloneQuestionBreakdown(mechanics.currentQuestionXPBreakdown)
      : createEmptyQuestionBreakdown(),
    guessSequence: [...mechanics.guessSequence],
    isCorrect: command.itemType === 'trivia' && mechanics.guessSequence.includes(0),
    wasAutoRevealed: mechanics.wasAutoRevealed,
    autoRevealReason: mechanics.autoRevealReason,
    itemType: command.itemType,
  };
  const attemptCounts = { ...mechanics.attemptCounts };
  const isCorrect = command.itemType === 'trivia' && mechanics.guessSequence.includes(0);

  if (mechanics.mode === 'endless' && command.itemType === 'trivia') {
    const attempts = mechanics.guessSequence.length;
    if (attempts === 1) attemptCounts.firstTry += 1;
    else if (attempts === 2) attemptCounts.secondTry += 1;
    else if (attempts === 3) attemptCounts.thirdTry += 1;
    else if (attempts >= 4) attemptCounts.fourthTry += 1;
  }

  return {
    mechanics: {
      ...mechanics,
      questionsAnswered: mechanics.questionsAnswered + 1,
      correctAnswers: mechanics.correctAnswers + (isCorrect ? 1 : 0),
      attemptCounts,
      runQuestionHistory: [...mechanics.runQuestionHistory, historyEntry],
      currentQuestionNetXP: 0,
      currentQuestionXPBreakdown: undefined,
      currentPollDistribution: undefined,
      currentPollSelectedOptionId: undefined,
      currentQuestionStatus: 'finalized',
    },
    outcome: { type: 'question-completed', historyEntry },
  };
}

function startNextQuestion(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'START_NEXT_QUESTION' }>
): MechanicsTransitionResult {
  return {
    mechanics: {
      ...mechanics,
      guessSequence: [],
      incorrectAttempts: [],
      strikesForCurrentQuestion: false,
      eliminatedDistractors: [],
      boltUsedThisQuestion: false,
      boltUsageCount: 0,
      questionStartedAt: command.questionStartedAt,
      correctAnswerElapsedSeconds: undefined,
      currentQuestionXPBreakdown: undefined,
      currentQuestionNetXP: 0,
      currentQuestionHastePenalties: [],
      currentPollDistribution: undefined,
      currentPollSelectedOptionId: undefined,
      ratingXPAwarded: false,
      focusLostDuringQuestion: false,
      wasAutoRevealed: false,
      autoRevealReason: undefined,
      currentQuestionStatus: 'active',
      hasStartedInitialQuestionClock: true,
    },
    outcome: { type: 'question-started' },
  };
}

function startInitialQuestionClock(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'START_QUESTION_CLOCK' }>
): MechanicsTransitionResult {
  if (mechanics.hasStartedInitialQuestionClock) {
    return {
      mechanics,
      outcome: { type: 'no-op', reason: 'initial-question-clock-already-started' },
    };
  }

  const questionIsUnanswered =
    mechanics.currentQuestionStatus === 'active' &&
    mechanics.guessSequence.length === 0 &&
    mechanics.currentPollSelectedOptionId === undefined &&
    mechanics.boltUsageCount === 0 &&
    mechanics.eliminatedDistractors.length === 0;
  if (!questionIsUnanswered) {
    return { mechanics, outcome: { type: 'no-op', reason: 'question-not-unanswered' } };
  }

  return {
    mechanics: {
      ...mechanics,
      questionStartedAt: command.questionStartedAt,
      hasStartedInitialQuestionClock: true,
    },
    outcome: { type: 'question-clock-started' },
  };
}

function hydrateActiveState(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'HYDRATE_ACTIVE_STATE' }>
): MechanicsTransitionResult {
  if (mechanics.guessSequence.length > 0) {
    return {
      mechanics,
      outcome: { type: 'no-op', reason: 'active-state-already-started' },
    };
  }

  return {
    mechanics: {
      ...mechanics,
      guessSequence: [...command.guessSequence],
      incorrectAttempts: [...command.incorrectAttempts],
      // Active-state persistence carries attempt evidence, not a completed
      // mechanics result with reconstructed XP. Keep the question playable.
      currentQuestionStatus: 'active',
    },
    outcome: { type: 'hydrated', target: 'active-state' },
  };
}

function persistedQuestionXP(fact: PersistedQuestionXPFact): number {
  const answered = fact.answered || (fact.guessCount ?? 0) > 0;
  if (!answered) return 0;
  if (fact.itemType === 'poll') return POLL_PARTICIPATION_XP;
  return fact.xpEarned ?? 0;
}

function hydrateSessionXP(
  mechanics: GameRunMechanicsState,
  command: Extract<GameRunMechanicsCommand, { type: 'HYDRATE_SESSION_XP' }>
): MechanicsTransitionResult {
  if (hasScoredXPComponents(mechanics.sessionXPBreakdown)) {
    return { mechanics, outcome: { type: 'no-op', reason: 'live-session-present' } };
  }

  const persistedQuestions = command.persistedQuestions ?? [];
  const persistedTotalXP = persistedQuestions.reduce(
    (total, fact) => total + persistedQuestionXP(fact),
    0
  );
  const hasPersistedAnswer =
    persistedQuestions.some((fact) => fact.answered || (fact.guessCount ?? 0) > 0) ||
    command.sessionTotalXP !== undefined;
  const sessionTotalXP = command.sessionTotalXP ?? persistedTotalXP;
  const sessionXPBreakdown = command.sessionXPBreakdown
    ? cloneSessionBreakdown(command.sessionXPBreakdown)
    : mechanics.mode === 'daily_challenge'
      ? { ...createEmptySessionBreakdown(), correctXP: sessionTotalXP }
      : createEmptySessionBreakdown();
  const sessionXPBreakdownAvailable =
    command.sessionXPBreakdownAvailable ??
    (command.sessionXPBreakdown
      ? true
      : mechanics.mode === 'daily_challenge' || !hasPersistedAnswer);

  return {
    mechanics: {
      ...mechanics,
      sessionTotalXP,
      sessionXPBreakdown,
      sessionXPBreakdownAvailable,
    },
    outcome: { type: 'hydrated', target: 'session-xp' },
  };
}

export function transitionGameRunMechanics(
  mechanics: GameRunMechanicsState,
  command: GameRunMechanicsCommand
): MechanicsTransitionResult {
  switch (command.type) {
    case 'ANSWER_SELECTED':
      return answerSelected(mechanics, command);
    case 'POLL_ANSWERED':
      return pollAnswered(mechanics, command);
    case 'SUBMIT_RATING':
      return submitRating(mechanics, command);
    case 'USE_BOLT':
      return applyBolt(mechanics, command);
    case 'FOCUS_LOST':
      if (mechanics.focusLostDuringQuestion) {
        return { mechanics, outcome: { type: 'focus-lost' } };
      }
      return {
        mechanics: { ...mechanics, focusLostDuringQuestion: true },
        outcome: { type: 'focus-lost' },
      };
    case 'SET_DIFFICULTY':
      return setDifficulty(mechanics, command);
    case 'AUTO_REVEAL_COMPLETE':
      return completeAutoReveal(mechanics);
    case 'START_QUESTION_CLOCK':
      return startInitialQuestionClock(mechanics, command);
    case 'COMPLETE_CURRENT_QUESTION':
      return completeCurrentQuestion(mechanics, command);
    case 'START_NEXT_QUESTION':
      return startNextQuestion(mechanics, command);
    case 'HYDRATE_ACTIVE_STATE':
      return hydrateActiveState(mechanics, command);
    case 'HYDRATE_SESSION_XP':
      return hydrateSessionXP(mechanics, command);
    case 'RESET_RUN':
      return {
        mechanics: createGameRunMechanics({
          mode: mechanics.mode,
          difficulty: mechanics.difficulty,
          questionStartedAt: command.questionStartedAt,
        }),
        outcome: { type: 'reset' },
      };
  }
}

export function isGameOver(mechanics: GameRunMechanicsState): boolean {
  return mechanics.mode === 'endless' && mechanics.strikes >= 3;
}

export function getPendingAutoRevealReason(
  mechanics: GameRunMechanicsState
): AutoRevealReason | undefined {
  return mechanics.currentQuestionStatus === 'auto_reveal_pending'
    ? mechanics.autoRevealReason
    : undefined;
}

export function getGameRunMechanicsView(mechanics: GameRunMechanicsState): GameRunMechanicsView {
  return {
    guessSequence: [...mechanics.guessSequence],
    incorrectAttempts: [...mechanics.incorrectAttempts],
    currentStreak: mechanics.currentStreak,
    longestStreak: mechanics.longestStreak,
    strikes: mechanics.strikes,
    strikesForCurrentQuestion: mechanics.strikesForCurrentQuestion,
    bolts: mechanics.bolts,
    eliminatedDistractors: [...mechanics.eliminatedDistractors],
    boltUsedThisQuestion: mechanics.boltUsedThisQuestion,
    boltUsageCount: mechanics.boltUsageCount,
    questionsAnswered: mechanics.questionsAnswered,
    correctAnswers: mechanics.correctAnswers,
    attemptCounts: { ...mechanics.attemptCounts },
    questionStartTime: mechanics.questionStartedAt,
    correctAnswerElapsedSeconds: mechanics.correctAnswerElapsedSeconds,
    xpEventId: mechanics.xpEventId,
    currentQuestionXPBreakdown: mechanics.currentQuestionXPBreakdown
      ? cloneQuestionBreakdown(mechanics.currentQuestionXPBreakdown)
      : undefined,
    currentQuestionHastePenalties: [...mechanics.currentQuestionHastePenalties],
    sessionTotalXP: mechanics.sessionTotalXP,
    sessionXPBreakdown: cloneSessionBreakdown(mechanics.sessionXPBreakdown),
    sessionXPBreakdownAvailable: mechanics.sessionXPBreakdownAvailable,
    currentQuestionNetXP: mechanics.currentQuestionNetXP,
    lastAnswerWasHasty: mechanics.lastAnswerWasHasty,
    lastRoundXPEarned: mechanics.lastXPEvent?.amount,
    lastRoundXPBreakdown: mechanics.lastXPEvent?.breakdown
      ? cloneQuestionBreakdown(mechanics.lastXPEvent.breakdown)
      : undefined,
    runQuestionHistory: mechanics.runQuestionHistory.map((entry) => ({
      ...entry,
      breakdown: cloneQuestionBreakdown(entry.breakdown),
      guessSequence: [...entry.guessSequence],
    })),
    currentPollDistribution: mechanics.currentPollDistribution?.map((entry) => ({ ...entry })),
    currentPollSelectedOptionId: mechanics.currentPollSelectedOptionId,
    ratingXPAwarded: mechanics.ratingXPAwarded,
    focusLostDuringQuestion: mechanics.focusLostDuringQuestion,
    wasAutoRevealed: mechanics.wasAutoRevealed,
    autoRevealReason: mechanics.autoRevealReason,
    lastRewardStreak: mechanics.lastRewardStreak,
    hasReceivedInitialBoltBonus: mechanics.hasReceivedInitialBoltBonus,
  };
}
